import getCookie from '@/utils/GetCookie'

let sessionTimer = null
let lastRequestID = getCookie('bilderlings_admin_last_request_v1') || 'INITIAL'
let lastTimestamp = Date.parse(sessionStorage.getItem('bilderlings_admin_last_request_timestamp_v1'))

if (isNaN(lastTimestamp)) { // could be NaN
  lastTimestamp = Date.now()
}

const state = () => ({
  admin: null,
  sessionTimeout: false,
  sessionTimeLeft: 0,
  sessionCheckEveryMs: 3000,
  // sessionModalThresholdMs: 15 * 1000,
  // sessionModalMaxMs: 30 * 1000
  sessionModalThresholdMs: 10 * 60 * 1000,
  sessionModalMaxMs: 15 * 60 * 1000
})

const getters = {
  admin (state) {
    return state.admin
  },
  adminAuthorities (state) {
    if (state.admin == null) {
      return {}
    }

    return state.admin.authorities
      .reduce((authorities, value) => {
        const separatorIndex = value.lastIndexOf(':')
        const permission = value.slice(separatorIndex + 1)
        const authority = value.slice(0, separatorIndex)

        authorities[authority] = permission
        return authorities
      }, {})
  },
  hasAuthorityWritePermission (state, getters) {
    return (authority) => {
      return getters.hasAuthorityPermission(authority, PERMISSIONS.WRITE)
    }
  },
  hasAuthorityReadPermission (state, getters) {
    return (authority) => {
      return getters.hasAuthorityPermission(authority, PERMISSIONS.READ)
    }
  },
  hasAuthorityPermission (state, getters) {
    return (authority, permission) => {
      if (permission === PERMISSIONS.READ && getters.adminAuthorities[authority] === PERMISSIONS.WRITE) {
        return true
      }

      return getters.adminAuthorities[authority] === permission
    }
  },
  hasRole (state) {
    return (...roles) => {
      return state.admin != null && roles.flat().some((role) => state.admin.authorities.includes(role))
    }
  },
  hasGroup (state) {
    return groupId => {
      return state.admin != null && (state.admin.groups.length === 0 || state.admin.groups.some(({ id }) => String(id) === String(groupId)))
    }
  },
  sessionTimeout (state) {
    return state.sessionTimeout
  },
  sessionTimeLeft (state) {
    return state.sessionTimeLeft
  }
}

const actions = {
  setAdmin ({ commit }, admin) {
    commit('setAdmin', admin)
  },
  startSessionTimer ({ commit, state, dispatch }, sessionParams) {
    if (sessionParams != null) {
      commit('setSessionParams', {
        sessionCheckEveryMs: !isNaN(sessionParams.sessionCheckEveryMs) ? sessionParams.sessionCheckEveryMs : state.sessionCheckEveryMs,
        sessionModalThresholdMs: !isNaN(sessionParams.sessionModalThresholdMs) ? sessionParams.sessionModalThresholdMs : state.sessionModalThresholdMs,
        sessionModalMaxMs: !isNaN(sessionParams.sessionModalMaxMs) ? sessionParams.sessionModalMaxMs : state.sessionModalMaxMs
      })
    }

    dispatch('startSessionCheck')
  },
  // @todo Refactor this crap, move to default layout
  startSessionCheck ({ commit, state, dispatch }) {
    lastTimestamp = Date.now()

    sessionStorage.setItem('bilderlings_admin_last_request_timestamp_v1', lastTimestamp)

    const onTimerFunc = () => {
      if (state.admin == null) {
        dispatch('stopSessionTimer')
      }

      const newRequestID = getCookie('bilderlings_admin_last_request_v1') || 'INITIAL'

      if (lastRequestID !== newRequestID) {
        lastTimestamp = Date.now()
        lastRequestID = newRequestID

        sessionStorage.setItem('bilderlings_admin_last_request_timestamp_v1', lastTimestamp)

        commit('sessionTimeLeft', state.sessionModalMaxMs)

        return
      }

      const difference = Math.max(Math.ceil((Date.now() - lastTimestamp) / 1000) * 1000, 0)
      const remaining = state.sessionModalMaxMs - difference

      commit('sessionTimeLeft', remaining)

      if (difference <= state.sessionModalThresholdMs) {
        // hide popup, if it is active (could happen, if somebody extended session in another tab!)
        if (state.sessionTimeout) {
          dispatch('resetSessionTimer')
        }
        return
      }

      if (remaining > 0 && !state.sessionTimeout) {
        commit('setSessionTimeout', true)
      }
    }

    sessionTimer = setInterval(onTimerFunc, state.sessionCheckEveryMs)
  },
  stopSessionTimer () {
    clearTimeout(sessionTimer)
  },
  resetSessionTimer ({ state, commit }) {
    commit('setSessionTimeout', false)
    commit('sessionTimeLeft', state.sessionModalMaxMs)
  }
}

const mutations = {
  setAdmin (state, admin) {
    state.admin = admin
  },
  setSessionTimeout (state, active) {
    state.sessionTimeout = active
  },
  sessionTimeLeft (state, timeLeft) {
    state.sessionTimeLeft = timeLeft
  },
  setSessionParams (state, params) {
    Object.assign(state, {
      sessionCheckEveryMs: params.sessionCheckEveryMs,
      sessionModalThresholdMs: params.sessionModalThresholdMs,
      sessionModalMaxMs: params.sessionModalMaxMs
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export const PERMISSIONS = {
  READ: 'read',
  WRITE: 'write'
}

export const AUTHORITY_NAMESPACES = {
  ACCOUNT: 'account'
}

export const ROLES = {
  AML_READ: 'account:aml:read',
  AML_WRITE: 'account:aml:write',
  AML_SUPERVISOR: 'account:aml:supervisor',

  ACCOUNTANT_READ: 'account:accountant:read',
  ACCOUNTANT_WRITE: 'account:accountant:write',
  ACCOUNTANT_SUPERVISOR: 'account:accountant:supervisor',
  ACCOUNTANT_SWIFT: 'account:accountant:swift',

  FEE_MANAGER: 'account:fees:write',

  SUPPORT_READ: 'account:support:read',
  SUPPORT_WRITE: 'account:support:write',
  HEAD_SUPPORT: 'account:head-support',

  MANAGER: 'account:manager',
  MANAGER_LEAD: 'account:manager_lead',

  MARKETING: 'account:marketing',

  REFERRAL: 'account:referral',

  REFERRAL_PROGRAM_READ: 'account:referralprogram:read',
  REFERRAL_PROGRAM_WRITE: 'account:referralprogram:write',

  FRAUD_MONITORING_READ: 'account:fraudmonitoring:read',
  FRAUD_MONITORING_WRITE: 'account:fraudmonitoring:write',

  COUNTRY_GROUPS_MANAGER: 'account:countrygroups:manager',

  RATES_WRITE: 'account:rates:write',

  CARD_DEPARTMENT_WRITE: 'account:card-department:write',

  ESIM_REFUND_WRITE: 'account:esim-refund:create',

  CHANNEL_READ: 'account:channels:read',
  CHANNEL_WRITE: 'account:channels:write'
}

export const BASE_ACCOUNT_ROLES = Object.values(ROLES).filter(role => ![ROLES.REFERRAL, ROLES.REFERRAL_PROGRAM_READ, ROLES.REFERRAL_PROGRAM_WRITE, ROLES.MARKETING, ROLES.FRAUD_MONITORING_READ, ROLES.FRAUD_MONITORING_WRITE].includes(role))

export const AUTHORITIES = {
  ACCOUNT_CURRENCY_CONVERSION: [AUTHORITY_NAMESPACES.ACCOUNT, 'currency_conversion'].join(':'),
  ACCOUNT_REQUISITES: [AUTHORITY_NAMESPACES.ACCOUNT, 'requisites'].join(':')
}

export const SUPERVISOR_PAYMENT_ACTIONS = {
  RETURN_TO_HOLD: 'RETURN_TO_HOLD',
  RETURN_PAYMENT: 'RETURN_PAYMENT',
  CHANGE_BANK_SWIFT: 'CHANGE_BANK_SWIFT',
  FORCE_TO_SEPA: 'FORCE_TO_SEPA'
}
